import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { useUser } from "./user/utils/user";

import { RegisterServiceWorker } from "./RegisterServiceWorker";
import Alert from "./common/components/Alert";
import Footer from "./common/components/Footer";
import Header from "./common/components/Header";
import ImpersonationBanner from "./common/components/ImpersonationBanner";
import InstallPrompt from "./common/components/InstallPrompt";
import { RoleClientSelectionProvider } from "./common/contexts/RoleClientSelectionContext";
import commonRoutes from "./common/routes";
import { setLanguage } from "./common/utils/helpers";
import dataRoutes from "./data/routes";
import delineation2Routes from "./delineation2/routes";
import delineationRoutes from "./delineation/routes";
import "./i18n";
import landcover2Routes from "./landcover2/routes";
import landcover3Routes from "./landcover3/routes";
import landcover4Routes from "./landcover4/routes";
import landcoverRoutes from "./landcover/routes";
import CheckPermissions from "./user/components/CheckPermissions";
import userRoutes from "./user/routes";

const roleRouteMap = {
  Data: dataRoutes,
  Delineation: delineationRoutes,
  Delineation2: delineation2Routes,
  Landcover: landcoverRoutes,
  Landcover2: landcover2Routes,
  Landcover3: landcover3Routes,
  Landcover4: landcover4Routes,
};

function prefixRoutes(prefix, routes, role, client) {
  return routes.map((r) => ({
    ...r,
    path: `${prefix}/${r.path}`,
    role,
    client,
  }));
}

export default function App() {
  const [alert, setAlert] = useState({ show: false });
  const showAlert = (alert) => setAlert({ ...alert, show: true });
  const { user } = useUser();

  useEffect(() => {
    var language = user?.user?.language || window.localStorage.defaultLanguage;
    setLanguage(language);
  }, [user]);

  const routes = useMemo(() => {
    let allRoutes = [
      ...prefixRoutes("/", commonRoutes),
      ...prefixRoutes("/users", userRoutes),
    ];

    // Always add the default Data/ocp routes
    allRoutes.push(...prefixRoutes("/data-ocp", dataRoutes, "Data", "ocp"));

    const roleClients = user?.user?.role_clients || [];

    roleClients.forEach(({ role_name, client_name }) => {
      if (role_name === "Data" && client_name === "ocp") {
        return;
      }
      const base = roleRouteMap[role_name];
      if (base) {
        const prefix = `/${role_name.toLowerCase()}-${client_name.toLowerCase()}`;
        allRoutes.push(...prefixRoutes(prefix, base, role_name, client_name));
      }
    });

    return allRoutes;
  }, [user]);

  return (
    <RoleClientSelectionProvider>
      <ImpersonationBanner showAlert={showAlert} />
      <div className="flex flex-col h-screen w-full">
        <Header />
        <div className="w-full px-4 sm:px-6 lg:px-8 absolute mt-2">
          <div className="mx-auto table">
            <Alert {...{ ...alert, setAlert: setAlert }} />
          </div>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 mt-8 sm:mt-12 lg:mt-16 mb-auto">
          <Routes>
            {routes.map((r) => (
              <Route
                key={r.path}
                path={r.path}
                element={
                  <CheckPermissions
                    openAccess={r.openAccess}
                    role={r.role}
                    client={r.client}
                    showAlert={showAlert}
                  >
                    <r.element showAlert={showAlert} />
                  </CheckPermissions>
                }
              />
            ))}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
        <Footer />
        <InstallPrompt />
        <RegisterServiceWorker />
      </div>
    </RoleClientSelectionProvider>
  );
}
